import { api } from '../utils/api';
import type { CaseProfilePayload } from '../components/shared/caseProfile';

/**
 * CT-0 0.7 — the posture chip's SINGLE source of truth.
 *
 * Reads `GET /api/v1/claims/{caseId}/case-profile` — the resolve() SSoT that
 * runs server-side — and NOT any derived/embedded `case_profile` field carried
 * by another payload (which can be stale). Returns the payload only when it is a
 * valid v1.0 profile; otherwise `null` so the chip renders nothing (missing /
 * legacy profile) and the header keeps its fallback.
 *
 * `options` mirrors what the workspace shell already passes to `api.get` — the
 * attorney path relies on the auto-injected Clerk token (no options), the
 * accountant path passes its own `{ headers }`.
 */
export async function getCaseProfile(
  caseId: string,
  options?: { headers?: Record<string, string>; skipAuth?: boolean },
): Promise<CaseProfilePayload | null> {
  try {
    const res = await api.get<CaseProfilePayload>(
      `/api/v1/claims/${caseId}/case-profile`,
      // Never surface a toast for a background header fetch.
      { showErrorToast: false, ...options },
    );
    return res && res.schema_version === '1.0' ? res : null;
  } catch {
    // On any error (404 / auth / network) the chip simply does not render.
    return null;
  }
}

/**
 * Carrier/adjuster CONTACT fields collected on the case (Intake → Parties) and saved into
 * `data.resolved_case_metadata`, which the backend `_inject_case_metadata` reads so every
 * deliverable (demand / sworn PoL / package) fills them without per-generation re-entry.
 *
 * Only the fields passed are written; sending `""` clears one — the backend then leaves the
 * document [PLACEHOLDER] and the provenance gate keeps 412-ing it (these are real fields, never
 * fabricated). PATCH `/api/v1/reports/drafts/{caseId}/case-contacts`.
 */
export interface CaseContacts {
  adjuster_name?: string;
  carrier_address?: string;
  claims_mailing_address?: string;
  carrier_phone?: string;
}

export async function saveCaseContacts(
  caseId: string,
  contacts: CaseContacts,
  options?: { headers?: Record<string, string> },
): Promise<{ success: boolean; saved: CaseContacts }> {
  return api.patch<{ success: boolean; saved: CaseContacts }>(
    `/api/v1/reports/drafts/${caseId}/case-contacts`,
    contacts,
    options,
  );
}

/**
 * FL-3.6 confirm-on-finalize (owner decision C, 2026-09-11). Attorney-side CONFIRM of the claim
 * posture: `PATCH /api/v1/attorney/case/{caseId}/claim-type` (firm-gated, Clerk auto-token) writes
 * a CONFIRMED case_profile to the db.drafts SoT (server-stamped `source="attorney"`, set_by/set_at)
 * so the resolver's `posture_confirmed` flips true and the finalize sign-off unlocks.
 *
 * IMPORTANT: `success` now reflects whether the SoT write actually took (`false` when it did not,
 * with a `message` explaining why). Callers MAY trust the flag, but re-reading `posture_confirmed`
 * after the call remains recommended as defense-in-depth.
 */
export async function confirmAttorneyPosture(
  caseId: string,
  posture: 'first_party' | 'third_party',
): Promise<{ success: boolean; claim_type: string; sot_written?: boolean; message?: string | null }> {
  return api.patch<{ success: boolean; claim_type: string; sot_written?: boolean; message?: string | null }>(
    `/api/v1/attorney/case/${caseId}/claim-type`,
    { claim_type: posture },
  );
}

export default getCaseProfile;
